/*
 * ---------------------------------------------------------------------------
 *
 * COPYRIGHT (c) 2023 IBS Software Canada Inc. All Rights Reserved.
 *
 * The copyright to the computer program(s) herein is the property of
 * IBS Software Canada Inc. The program(s) may be used and/or copied
 * only with the written permission from IBS Software Canada Inc.
 * or in accordance with the terms and conditions stipulated in the
 * agreement/contract under which the program(s) have been supplied.
 *
 * Date  : 2023-11-27
 *
 * ---------------------------------------------------------------------------
 */

import axios from 'axios';
import {
  AuthorizationRequest,
  AuthorizationServiceConfiguration,
  FetchRequestor,
  GRANT_TYPE_REFRESH_TOKEN,
  GRANT_TYPE_AUTHORIZATION_CODE,
  TokenRequest,
  BaseTokenRequestHandler
} from '@openid/appauth';
import oidcAuthConfig from '../../oidcAuthConfig';
import { ACCESS_TOKEN, REFRESH_TOKEN, STAFFID, APPAUTH_SUFFIX } from '../../data/constants';


function prepareToken(request, response) {
  let extras = null;
  if (request && request.internal) {
    extras = {};
    extras.code_verifier = request.internal.code_verifier;
  }

  return new TokenRequest({
    client_id: oidcAuthConfig.clientId,
    redirect_uri: oidcAuthConfig.redirectURL,
    grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
    code: response.code,
    refresh_token: undefined,
    extras
  });
}

function prepareRefreshToken() {
  return new TokenRequest({
    client_id: oidcAuthConfig.clientId,
    redirect_uri: oidcAuthConfig.redirectURL,
    grant_type: GRANT_TYPE_REFRESH_TOKEN,
    refresh_token: sessionStorage.getItem(REFRESH_TOKEN),
  });
}

function performUserInfoRequest() {
  return axios({
    method: 'get',
    url: `${oidcAuthConfig.OPServer}/${oidcAuthConfig.userInfoEndpoint}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionStorage.getItem(ACCESS_TOKEN)}`
    },
  });
}

function findValueInUserInfo(userInfoResponse, key) {
  if (typeof userInfoResponse !== 'object' || userInfoResponse === null) {
    return null;
  }
  if (key in userInfoResponse) {
    return userInfoResponse[key];
  }
  for (const k in userInfoResponse) {
    const found = findValueInUserInfo(userInfoResponse[k], key);
    if (found !== null) {
      return found; // Return the found value
    }
  }
  return null;
}

function performRequest(tokenRequest) {
  const tokenHandler = new BaseTokenRequestHandler(new FetchRequestor());
  return AuthorizationServiceConfiguration.fetchFromIssuer(oidcAuthConfig.OPServer, new FetchRequestor())
    .then((oResponse) => {
      const configuration = oResponse;
      return tokenHandler.performTokenRequest(configuration, tokenRequest)
        .then((resp) => {
          sessionStorage.setItem(ACCESS_TOKEN, resp.accessToken);
          sessionStorage.setItem(REFRESH_TOKEN, resp.refreshToken);
          // We only fetch the user info once
          if (sessionStorage.getItem(STAFFID) === null) {
            return performUserInfoRequest().then((userInfoResponse) => {
              let oblixId;
              const oblixIdNameInUserInfo = oidcAuthConfig.oblixIdNameInUserInfo !== '' ? oidcAuthConfig.oblixIdNameInUserInfo : oidcAuthConfig.oblixIdFieldName;
              oblixId = findValueInUserInfo(userInfoResponse, oblixIdNameInUserInfo);
              if (oblixId !== undefined && oblixId !== null) {
                if (Array.isArray(oblixId) && oblixId.length > 0) {
                  oblixId = oblixId[0];
                }
                if (!sessionStorage.getItem(STAFFID)) {
                  sessionStorage.setItem(STAFFID, oblixId);
                }
              }
              const isAdminNameInUserInfo = oidcAuthConfig.isAdminNameInUserInfo !== '' ? oidcAuthConfig.isAdminNameInUserInfo : oidcAuthConfig.isAdminFieldName;
              const isAdmin = findValueInUserInfo(userInfoResponse, isAdminNameInUserInfo);
              if (isAdmin !== undefined && isAdmin !== null) {
                if (Array.isArray(isAdmin) && isAdmin.includes(oidcAuthConfig.isAdminFieldValue)) {
                  sessionStorage.setItem(oidcAuthConfig.isAdminFieldName, oidcAuthConfig.isAdminFieldValue);
                } else if (!Array.isArray(isAdmin) && isAdmin === oidcAuthConfig.isAdminFieldValue) {
                  sessionStorage.setItem(oidcAuthConfig.isAdminFieldName, oidcAuthConfig.isAdminFieldValue);
                }
              }
            }
            );
          }
        })
        .catch(() => {
          // Preserve language preference
          const tempLng = localStorage.getItem('lng') || sessionStorage.getItem('lng');
          sessionStorage.clear();
          // Restore language preference
          if (tempLng) {
            localStorage.setItem('lng', tempLng);
            sessionStorage.setItem('lng', tempLng);
          }
          window.location.href = '/login';
          window.location.reload();
        });
    }).catch(() => {
      console.log('error occured in token send');
    });
}

function clearLocalStorageFromTemporaryAuthRequest() {
  console.log('Clearing session storage from Authorization Requests');
  const itemsToBeRemoved = [];
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const item = sessionStorage.key(index);
    if (item.includes(APPAUTH_SUFFIX)) {
      itemsToBeRemoved.push(item);
    }
  }
  itemsToBeRemoved.forEach((item1) => {
    sessionStorage.removeItem(item1);
  });
}


function performTokenRequest(response, request) {
  const tokenRequest = prepareToken(request, response);
  return performRequest(tokenRequest);
}

function performRefreshTokenRequest() {
  const refreshTokenRequest = prepareRefreshToken();

  return performRequest(refreshTokenRequest);
}

function performAuthorizationRequest(authorizationHandler) {
  AuthorizationServiceConfiguration.fetchFromIssuer(oidcAuthConfig.OPServer, new FetchRequestor())
    .then((response) => {
      const authRequest = new AuthorizationRequest({
        client_id: oidcAuthConfig.clientId,
        redirect_uri: oidcAuthConfig.redirectURL,
        scope: oidcAuthConfig.scope,
        response_type: AuthorizationRequest.RESPONSE_TYPE_CODE,
        state: undefined,
        extras: { appId: 'crewInterface' }
      });
      authorizationHandler.performAuthorizationRequest(response, authRequest);
    });
}

export { performTokenRequest, performAuthorizationRequest, performRefreshTokenRequest, clearLocalStorageFromTemporaryAuthRequest };
